import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff } from 'lucide-react';

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', name: '', company: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success('Account created! Welcome to The Human.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080A] flex items-center justify-center px-6">
      <div className="noise-overlay" />
      <div className="w-full max-w-sm animate-fade-in-up opacity-0">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="signup-logo">
          <Sparkles className="w-6 h-6 text-[#CDFE65]" />
          <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Chivo' }}>THE HUMAN</span>
        </Link>
        <h1 className="text-3xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo' }}>Create account</h1>
        <p className="text-sm text-white/40 mb-8">Start generating in 30 seconds</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Name</label>
              <input type="text" value={form.name} onChange={e => update('name', e.target.value)} className="forge-input w-full" placeholder="Jane" data-testid="signup-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Company</label>
              <input type="text" value={form.company} onChange={e => update('company', e.target.value)} className="forge-input w-full" placeholder="Acme Inc" data-testid="signup-company-input" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Email</label>
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className="forge-input w-full" placeholder="you@company.com" required data-testid="signup-email-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 font-mono mb-1.5 block">Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)} className="forge-input w-full pr-10" placeholder="Min 8 characters" required minLength={8} data-testid="signup-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full text-sm" data-testid="signup-submit-btn">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>
        <p className="text-sm text-white/30 mt-6 text-center">
          Already have an account? <Link to="/login" className="text-[#CDFE65] hover:underline" data-testid="signup-login-link">Log in</Link>
        </p>
      </div>
    </div>
  );
}
